import { useState, useCallback, useEffect, useRef } from "react";
import { CaraChatPanel } from "./CaraChatPanel";
import { CaraSessionList } from "./CaraSessionList";
import { CaraAssetTimeline } from "./CaraAssetTimeline";
import type { TimelineAsset } from "./CaraAssetTimeline";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "~/components/ui/dialog";

// ── Types ────────────────────────────────────────────────────────────────────

type CaraSession = {
  id: string;
  title: string | null;
  status: "active" | "archived";
  messageCount: number;
  createdAt: string | Date;
  updatedAt: string | Date;
};

export type CaraStudioProps = {
  /** Pre-loaded sessions from a server loader. When omitted, the component
   *  fetches sessions on mount via the resource route. */
  initialSessions?: CaraSession[];
  /** Optional className applied to the root container. */
  className?: string;
  /** Override the Cara streaming API endpoint (default: /resources/cara). */
  apiUrl?: string;
  /** When true, sessions are ephemeral (client-only) and server persistence
   *  is skipped. Used for public demo kiosks. */
  demoMode?: boolean;
  /** Audience mode — affects welcome copy, suggestion chips, and available actions. */
  mode?: "designer" | "buyer";
  /** If provided, automatically sent as the first message when the chat
   *  panel mounts with no existing messages. Useful for demo prompt starters. */
  initialPrompt?: string;
  /** Creator name for demo gallery attribution. */
  creatorName?: string;
  /** Seed the active session from an external source (e.g. URL param). */
  initialSessionId?: string | null;
  /** Called whenever the active session changes (new session, switch, etc.). */
  onSessionChange?: (sessionId: string) => void;
  /** Full UIMessage[] from the server for session restoration (includes tool parts). */
  restoredUIMessages?: unknown[];
  /** Pre-loaded artifacts from the server for session restoration (populates timeline). */
  restoredArtifacts?: Array<{ id: string; type: string; data: Record<string, unknown>; imageUrl?: string; createdAt: string }>;
};

// ── Icons (inline SVGs to keep the component dependency-free) ────────────────

function MenuIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path fillRule="evenodd" d="M2 4.75A.75.75 0 0 1 2.75 4h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 4.75Zm0 10.5a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5a.75.75 0 0 1-.75-.75ZM2 10a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 10Z" clipRule="evenodd" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5">
      <path d="M8 2a.75.75 0 0 1 .75.75v4.5h4.5a.75.75 0 0 1 0 1.5h-4.5v4.5a.75.75 0 0 1-1.5 0v-4.5h-4.5a.75.75 0 0 1 0-1.5h4.5v-4.5A.75.75 0 0 1 8 2Z" />
    </svg>
  );
}

function PanelsIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M7 3.5A1.5 1.5 0 0 1 8.5 2h3A1.5 1.5 0 0 1 13 3.5v13a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 7 16.5v-13ZM1 6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V6Zm14-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-2Z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5">
      <path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z" />
    </svg>
  );
}

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z" />
    </svg>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

let demoCounter = 0;

export function CaraStudio({ initialSessions, className, apiUrl = "/resources/cara", demoMode = false, mode = "designer", initialPrompt, creatorName, initialSessionId, onSessionChange, restoredUIMessages, restoredArtifacts }: CaraStudioProps) {
  const [sessions, setSessions] = useState<CaraSession[]>(() => {
    const base = initialSessions ?? [];
    if (demoMode && base.length === 0) {
      const id = initialSessionId ?? `demo_${Date.now().toString(36)}_${++demoCounter}`;
      const msgCount = restoredUIMessages?.length ?? 0;
      return [{ id, title: null, status: "active" as const, messageCount: msgCount, createdAt: new Date(), updatedAt: new Date() }];
    }
    return base;
  });
  const [activeSessionId, setActiveSessionId] = useState<string | null>(
    initialSessionId ?? sessions[0]?.id ?? null,
  );

  // Sync with new initialSessions when they change (e.g. route re-navigation)
  const prevInitialRef = useRef(initialSessions);
  useEffect(() => {
    if (prevInitialRef.current !== initialSessions) {
      prevInitialRef.current = initialSessions;
      const next = initialSessions ?? [];
      setSessions(next);
      if (!next.some((s) => s.id === activeSessionId)) {
        setActiveSessionId(next[0]?.id ?? null);
      }
    }
  }, [initialSessions, activeSessionId]);

  // If no initialSessions were provided, fetch them on mount (skip in demo mode)
  const didFetchRef = useRef(false);
  useEffect(() => {
    if (demoMode || initialSessions != null || didFetchRef.current) return;
    didFetchRef.current = true;
    fetch(`${apiUrl}?intent=sessions`)
      .then((r) => r.json())
      .then((data) => {
        const fetched = (data.sessions ?? []) as CaraSession[];
        setSessions(fetched);
        setActiveSessionId(fetched[0]?.id ?? null);
      })
      .catch(() => {});
  }, [initialSessions, demoMode, apiUrl]);

  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [timelinePanelOpen, setTimelinePanelOpen] = useState(true);
  const [timelineAssets, setTimelineAssets] = useState<TimelineAsset[]>([]);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);

  useEffect(() => {
    if (!activeSessionId) {
      setTimelineAssets([]);
      return;
    }
    setTimelineAssets([]);
    setSelectedAssetId(null);
  }, [activeSessionId]);

  const onSessionChangeRef = useRef(onSessionChange);
  onSessionChangeRef.current = onSessionChange;
  const initialSessionIdRef = useRef(initialSessionId);
  const didNotifyRef = useRef(false);
  useEffect(() => {
    if (!activeSessionId) return;
    if (!didNotifyRef.current && activeSessionId === initialSessionIdRef.current) {
      didNotifyRef.current = true;
      return;
    }
    didNotifyRef.current = true;
    onSessionChangeRef.current?.(activeSessionId);
  }, [activeSessionId]);

  const refreshSessions = useCallback(() => {
    if (demoMode) return;
    fetch(`${apiUrl}?intent=sessions`)
      .then((r) => r.json())
      .then((data) => setSessions(data.sessions ?? []))
      .catch(() => {});
  }, [demoMode, apiUrl]);

  const handleNewSession = useCallback(async () => {
    if (demoMode) {
      const id = `demo_${Date.now().toString(36)}_${++demoCounter}`;
      const session: CaraSession = { id, title: null, status: "active", messageCount: 0, createdAt: new Date(), updatedAt: new Date() };
      setSessions((prev) => [session, ...prev]);
      setActiveSessionId(id);
      return;
    }
    setIsCreatingSession(true);
    try {
      const res = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intent: "create-session" }),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.sessionId) {
        setActiveSessionId(data.sessionId);
        refreshSessions();
      }
    } catch {
      // network error
    } finally {
      setIsCreatingSession(false);
    }
  }, [refreshSessions, demoMode, apiUrl]);

  const handleSelectSession = useCallback((id: string) => {
    setActiveSessionId(id);
    setSidebarOpen(false);
  }, []);

  const handleSessionCreated = useCallback(
    (id: string) => {
      if (demoMode) {
        const session: CaraSession = { id, title: null, status: "active", messageCount: 0, createdAt: new Date(), updatedAt: new Date() };
        setSessions((prev) => prev.some((s) => s.id === id) ? prev : [session, ...prev]);
      }
      setActiveSessionId(id);
      refreshSessions();
    },
    [refreshSessions, demoMode],
  );

  const handleRenameSession = useCallback(
    async (id: string, title: string) => {
      if (demoMode) {
        setSessions((prev) => prev.map((s) => s.id === id ? { ...s, title } : s));
        return;
      }
      try {
        const res = await fetch(apiUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ intent: "rename-session", sessionId: id, title }),
        });
        if (res.ok) refreshSessions();
      } catch {
        // network error
      }
    },
    [refreshSessions, demoMode, apiUrl],
  );

  const handleDeleteSession = useCallback(
    async (id: string) => {
      if (demoMode) {
        setSessions((prev) => prev.filter((s) => s.id !== id));
        if (activeSessionId === id) {
          setActiveSessionId(null);
          setTimelineAssets([]);
        }
        return;
      }
      try {
        const res = await fetch(apiUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ intent: "delete-session", sessionId: id }),
        });
        if (!res.ok) return;
        if (activeSessionId === id) {
          setActiveSessionId(null);
          setTimelineAssets([]);
        }
        refreshSessions();
      } catch {
        // network error
      }
    },
    [activeSessionId, refreshSessions, demoMode, apiUrl],
  );

  const handleAssetsChange = useCallback((assets: TimelineAsset[]) => {
    setTimelineAssets(assets);
  }, []);

  const chatScrollRef = useRef<HTMLDivElement>(null);

  const handleScrollToMessage = useCallback((messageIndex: number) => {
    const container = chatScrollRef.current;
    if (!container) return;
    const el = container.querySelector(`[data-message-index="${messageIndex}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  const handleAssetSelect = useCallback((assetId: string) => {
    setSelectedAssetId(assetId);
    const asset = timelineAssets.find((a) => a.id === assetId);
    if (asset) handleScrollToMessage(asset.messageIndex);
  }, [timelineAssets, handleScrollToMessage]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.shiftKey && e.key === "N") {
        e.preventDefault();
        handleNewSession();
      }
      if (mod && e.shiftKey && e.key === "O") {
        e.preventDefault();
        setSidebarOpen((prev) => !prev);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleNewSession]);

  const activeSession = sessions.find((s) => s.id === activeSessionId);

  return (
    <div className={`h-full flex flex-col ${className ?? ""}`}>
      {/* ── Top bar ── */}
      <div className="shrink-0 flex items-center justify-between border-b border-border h-11 px-2 lg:px-0 relative">
        {/* Left: session sidebar toggle + session info */}
        <div className="flex items-center gap-1 min-w-0">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="shrink-0 lg:ml-2"
            aria-label="Toggle sessions"
          >
            <MenuIcon />
          </Button>

          <div className="h-4 w-px bg-border mx-1 hidden lg:block" />

          <div className="flex items-center gap-2 min-w-0 px-1">
            <div className="h-5 w-5 rounded-full flex items-center justify-center shrink-0" style={{ background: "oklch(0.45 0.08 213 / 0.15)" }}>
              <div className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--teal-sapphire)" }} />
            </div>
            <span className="text-xs font-display text-foreground truncate">
              {activeSession?.title || "Cara AI"}
            </span>
            {activeSession && (
              <span className="text-[10px] text-muted-foreground/50 hidden sm:inline">
                {activeSession.messageCount > 0 ? `${activeSession.messageCount} messages` : "New session"}
              </span>
            )}
          </div>
        </div>

        {/* Center: Cara branding */}
        <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1.5 pointer-events-none">
          <SparkleIcon className="h-4 w-4 text-primary/30 hidden sm:block" />
          <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/40 font-medium">Cara</span>
        </div>

        {/* Right: timeline toggle + new session */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={handleNewSession}
            disabled={isCreatingSession}
            aria-label="New session"
            className="shrink-0"
          >
            {isCreatingSession ? (
              <span className="h-3.5 w-3.5 border border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <PlusIcon />
            )}
          </Button>

          <div className="h-4 w-px bg-border mx-1" />

          <Button
            variant={timelinePanelOpen ? "secondary" : "ghost"}
            size="icon-sm"
            onClick={() => setTimelinePanelOpen(!timelinePanelOpen)}
            aria-label="Toggle asset timeline"
            className="shrink-0 lg:mr-2"
          >
            <PanelsIcon />
          </Button>
        </div>
      </div>

      {/* ── Main area ── */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0 relative">
        {/* Session sidebar — slide-over */}
        {sidebarOpen && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px]"
              onClick={() => setSidebarOpen(false)}
            />
            <aside className="fixed left-0 top-0 bottom-0 z-50 w-72 bg-background border-r border-border shadow-xl animate-in slide-in-from-left duration-200">
              <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-medium">Sessions</p>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setSidebarOpen(false)}
                  aria-label="Close sessions"
                >
                  <CloseIcon />
                </Button>
              </div>
              <CaraSessionList
                sessions={sessions}
                activeSessionId={activeSessionId}
                onSelectSession={handleSelectSession}
                onNewSession={handleNewSession}
                onRenameSession={handleRenameSession}
                onDeleteSession={handleDeleteSession}
                isCreating={isCreatingSession}
              />
            </aside>
          </>
        )}

        {/* Chat panel — takes remaining space */}
        <main className="flex-1 min-w-0 min-h-0">
          <CaraChatPanel
            sessionId={activeSessionId}
            onSessionCreated={handleSessionCreated}
            onNewArtifact={undefined}
            onAssetsChange={handleAssetsChange}
            apiUrl={apiUrl}
            mode={mode}
            initialPrompt={initialPrompt}
            creatorName={creatorName}
            scrollContainerRef={chatScrollRef}
            restoredUIMessages={activeSessionId === initialSessionId ? restoredUIMessages : undefined}
            restoredArtifacts={activeSessionId === initialSessionId ? restoredArtifacts : undefined}
          />
        </main>

        {/* Asset timeline panel — right side (desktop) */}
        {timelinePanelOpen && (
          <aside className="hidden lg:flex w-80 xl:w-96 shrink-0 border-l border-border bg-background/50 flex-col">
            <CaraAssetTimeline
              assets={timelineAssets}
              selectedAssetId={selectedAssetId}
              onSelectAsset={handleAssetSelect}
            />
          </aside>
        )}

        {/* Mobile timeline — horizontal strip (not fixed, sits below chat) */}
        {timelinePanelOpen && timelineAssets.length > 0 && (
          <aside className="lg:hidden shrink-0 border-t border-border bg-background">
            <div className="flex items-center justify-between px-4 py-1.5">
              <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-medium">Assets</p>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setTimelinePanelOpen(false)}
                aria-label="Close timeline"
              >
                <svg viewBox="0 0 16 16" fill="currentColor" className="h-3 w-3">
                  <path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z" />
                </svg>
              </Button>
            </div>
            <div className="px-3 pb-2">
              <div className="flex gap-2.5 overflow-x-auto pb-1">
                {timelineAssets.filter((a) => a.dataUrl).map((asset) => (
                  <Dialog key={asset.id}>
                    <DialogTrigger asChild>
                      <button
                        onClick={() => handleAssetSelect(asset.id)}
                        className={`shrink-0 w-20 rounded-lg border overflow-hidden transition-all ${
                          selectedAssetId === asset.id ? "border-foreground/20 ring-1 ring-foreground/10" : "border-border/60"
                        }`}
                      >
                        <img src={asset.dataUrl} alt={asset.label} className="w-full aspect-square object-cover" loading="lazy" />
                        <p className="text-[8px] text-muted-foreground px-1 py-0.5 truncate">{asset.label}</p>
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-3xl p-0 overflow-hidden">
                      <img src={asset.dataUrl} alt={`${asset.label} — full size`} className="w-full" />
                    </DialogContent>
                  </Dialog>
                ))}
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
